<template>
  <ontos-kanban-board :context="childContext"
                      :workspace="workspace"
                      :filter="filter"
                      :items="items"
                      :reload-items="reloadOwner"
                      :initial-values="initialValues"
                      :link-created="linkCreated"
                      :add-in-lane="editing.link || editing.create ? openInLane : null"
                      :unlink-item="editing.unlink ? askUnlink : null"
                      :deletes="false"
                      :scope="scope"
                      :flush="flush"
                      :header-to="headerTo"
                      :known-candidates="knownCandidates"
                      @candidates="$emit('candidates', $event)"
                      @open-instance="openInstance"
                      @changed="$emit('changed')"
                      @no-dimensions="$emit('no-dimensions')"
                      @showMessage="$emit('showMessage', $event)"
                      @showErrorMessage="$emit('showErrorMessage', $event)">
    <!-- Os slots do cabeçalho do tabuleiro chegam de quem hospeda: a página põe o nome da
         propriedade e o botão de voltar; o bloco da ficha põe o cabeçalho dele inteiro, com as abas
         e as falhas embaixo. Todos, e não só o título: o que não for repassado some, porque aqui o
         cabeçalho é o do tabuleiro e o do bloco não existe. -->
    <template v-if="$slots.title" #title>
      <slot name="title"/>
    </template>
    <template v-if="$slots.tabs" #tabs>
      <slot name="tabs"/>
    </template>
    <!-- O "Adicionar" à frente dos controles de quem hospeda (o expandir da ficha, o voltar da
         página): solto, como na Lista da ficha e no "+ Criar" da faixa do caminho (decisão do
         usuário). Aqui, e não em cada hospedeiro: a ficha e a página expandida são o MESMO quadro, e
         a página tinha ficado sem ele (relato do usuário). -->
    <!-- Com o cabeçalho na linha das abas da ficha (`headerTo`), o "Adicionar" vai para a frente de
         tudo, antes até das ações do quadro (pedido do usuário) — pelo `order`, sem mudar o cabeçalho,
         que segue o mesmo na página expandida. Num `span`: a raiz do RelationshipAdd é o menu, e a
         classe nele iria para a caixa que se abre, e não para o botão. -->
    <!-- Com `addTo`, só o "Adicionar" vai para outro lugar: no telefone, a ficha o quer na linha das
         abas e o resto do cabeçalho na linha de baixo (pedido do usuário). Continua sendo o deste
         quadro — é ele que o "+" das raias abre, e é aqui que o adicionado entra. -->
    <template v-if="editable || $slots.controls" #controls>
      <Teleport :to="addTo || 'body'" :disabled="!addTo" defer>
        <span v-if="editable" :class="{'board-add--first': !!headerTo}">
          <ontos-relationship-add ref="add"
                                  :owner-context="ownerContext" :owner="owner" :property="property"
                                  :domains="domains" :reverse-infos="reverseInfo" :workspace="workspace"
                                  :target-read="knownCandidates"
                                  @added="onAdded($event)"
                                  @showMessage="$emit('showMessage', $event)"
                                  @showErrorMessage="$emit('showErrorMessage', $event)"/>
        </span>
      </Teleport>
      <slot name="controls"/>
    </template>
    <template v-if="$slots.below" #below>
      <slot name="below"/>
    </template>
  </ontos-kanban-board>

  <!-- O X de um card: a mesma pergunta e a mesma gravação do X da linha na Lista da ficha -->
  <ontos-relationship-unlink-dialog v-if="unlinking"
                                    :owner-context="ownerContext" :owner="owner" :property="property"
                                    :item="unlinking" :workspace="workspace"
                                    @unlinked="onUnlinked($event)"
                                    @closed="unlinking = null"
                                    @showMessage="$emit('showMessage', $event)"/>
</template>

<script lang="ts">
import {Component, Vue, Prop, toNative} from 'vue-facing-decorator'
import KanbanBoard from "@/components/live/instance/KanbanBoard.vue"
import RelationshipAdd from "@/components/live/instance/RelationshipAdd.vue"
import RelationshipUnlinkDialog from "@/components/live/instance/RelationshipUnlinkDialog.vue"
import IdeaProperty from "@/types/IdeaProperty"
import Plain from "@/types/Plain"
import {InstanceContext} from "@/types/InstanceContext"
import {Preview} from "@/utils/instancePreview"
import {EnhancedWorkpace} from "@/types/Workspace"
import {DomainItem, ReverseInfo} from "@/services/reverseRelationship"
import {asArray} from "@/utils/arrayUtils"
import {LaneAdd} from "@/utils/kanbanDimensions"
import {
  linkToOwner,
  needsLinkAfterCreate,
  relationshipEditing,
  relationshipFilter,
  relationshipInitialValues,
  relationshipScope,
  withRelated
} from "@/utils/relationshipKanban"

/**
 * O tabuleiro recortado aos itens de UM relacionamento de UMA instância — "as tarefas deste
 * projeto".
 *
 * Nasceu porque a mesma fiação estava escrita duas vezes, na página de rota própria e na ficha
 * da instância: o contexto da coleção de destino, o filtro, os valores com que um item nasce e
 * o vínculo pós-criação — este último um PATCH no dono, copiado inteiro nos dois lugares. As
 * regras puras já moravam juntas em `utils/relationshipKanban.ts`; o que faltava era a montagem
 * delas ter uma casa só.
 *
 * Pela mesma razão é aqui que moram adicionar e desvincular pelo quadro — o "Adicionar" do
 * cabeçalho, o "+" de cada raia e o X de cada card —, e não na ficha: a página expandida é o mesmo
 * quadro. O que entra e o que sai é aplicado ao dono que a tela já tem, sem reler, e devolvido a
 * quem o carregou pelo `update:owner`.
 *
 * Não hospeda o modal de ficha: quem abre o `open-instance` é quem usa este componente, porque
 * empilhar modal a partir daqui furaria a pilha que o Vuetify monta pela árvore do diálogo.
 */
@Component({
  emits: ['open-instance', 'changed', 'no-dimensions', 'candidates', 'update:owner', 'showMessage',
    'showErrorMessage'],
  name: 'ontos-relationship-board',
  components: {
    'ontos-kanban-board': KanbanBoard,
    'ontos-relationship-add': RelationshipAdd,
    'ontos-relationship-unlink-dialog': RelationshipUnlinkDialog
  }
})
class RelationshipBoard extends Vue {
  /** A instância DONA: system, idea e instance da ficha, não da coleção de destino. */
  @Prop({type: Object, required: true}) readonly ownerContext!: InstanceContext
  /**
   * A instância dona já carregada, com `_rels`.
   *
   * Precisa vir pronta: no múltiplo direto a lista de ids mora nela, e sem isso o filtro sairia
   * vazio e o quadro mostraria um recorte de nada.
   */
  @Prop({type: Object, default: null}) readonly owner!: any
  @Prop({type: Object as () => IdeaProperty, required: true}) readonly property!: IdeaProperty
  /** O que o servidor contou sobre o lado de lá — usado só no reverso. */
  @Prop({default: null}) readonly reverseInfo!: any
  @Prop({required: true}) readonly workspace!: EnhancedWorkpace
  /** Repassado: quem sabe se há folga em volta é quem hospeda. Ver o KanbanBoard. */
  @Prop({type: Boolean, default: false}) readonly flush!: boolean
  /** Onde desenhar o cabeçalho do quadro, se não acima das raias — ver o do KanbanBoard. */
  @Prop({type: String, default: ''}) readonly headerTo!: string
  /** Onde desenhar o "Adicionar", se não no cabeçalho do quadro: o seletor de um elemento de quem hospeda. */
  @Prop({type: String, default: ''}) readonly addTo!: string
  /** Os candidatos que a ficha guardou entre uma troca de aba e outra — ver o KanbanBoard. */
  @Prop({type: Object, default: null}) readonly knownCandidates!: any
  /** Os candidatos de cada relacionamento do DONO, por coleção — o `relDomains` da leitura dele. */
  @Prop({type: Object, default: () => ({})}) readonly domains!: { [ideaAlias: string]: DomainItem[] }
  /**
   * Adicionar e desvincular pelo quadro, se quem pode também puder. Desligado pela ficha na prévia,
   * quando os vinculados e os candidatos ainda não chegaram.
   */
  @Prop({type: Boolean, default: true}) readonly editable!: boolean
  /**
   * Como recarregar o dono, quando o vínculo pós-criação o altera.
   *
   * Função, e não evento, pela mesma razão que o `linkCreated` do tabuleiro é função: tem de
   * ser AGUARDADA antes de o quadro recarregar, senão o filho recém-criado ainda não estaria na
   * lista do dono e não cairia no filtro. Quem tem o dono em mão é quem o carregou.
   */
  // eslint-disable-next-line no-unused-vars
  @Prop({type: Function, default: null}) readonly reloadOwner!: (() => Promise<void>) | null

  /** O item que o X de um card pediu para desvincular, à espera da confirmação. */
  unlinking: any = null

  /** O quadro é da coleção de DESTINO; o dono só decide o recorte. */
  get childContext(): InstanceContext {
    return {
      system: this.ownerContext.system,
      idea: this.property.relationship!.idea
    } as InstanceContext
  }

  /**
   * O recorte: quais itens da coleção de destino pertencem a este dono.
   *
   * Aqui ele não vai ao servidor — os itens já vêm prontos —, mas continua sendo passado porque
   * é ele que diz qual dimensão está presa: o campo que aponta de volta para o dono tem o mesmo
   * valor em todos estes itens, e agrupá-lo daria uma raia só (ver `fixedByFilter`).
   */
  get filter(): Plain {
    return relationshipFilter(this.property, this.owner, this.ownerContext.instance!)
  }

  /**
   * Os itens do relacionamento, lidos do dono que a tela já carregou.
   *
   * O quadro não os relê: quem carrega o dono pede `relProperties` e `depth: 2`, e com isso cada
   * item vem inteiro — propriedades, situação, os carimbos de data de que o prazo das raias vive
   * e os relacionamentos DELE, que é do que as raias de uma dimensão de relacionamento precisam.
   * Mesmo conjunto que o filtro traria: no reverso o servidor preenche o `_rels` com quem
   * pertence ao dono, e no múltiplo direto com a lista que mora nele.
   *
   * Lista CRUA, sem esconder os encerrados: quem decide o que fica à vista no quadro é o prazo
   * de cada raia, e um item escondido aqui sumiria também da conta do "+3".
   */
  get items(): any[] {
    const rel = this.owner && this.owner._rels && this.owner._rels[this.property.alias]
    return asArray(rel)
  }

  get info(): ReverseInfo | null {
    return (this.reverseInfo && this.reverseInfo[this.property.alias]) || null
  }

  get initialValues(): Plain | null {
    return relationshipInitialValues(this.property, this.ownerContext.instance!, this.info)
  }

  /** Quem pode o quê — a regra do `relationshipEditing`, a mesma da Lista da ficha. */
  get editing() {
    const can = relationshipEditing(this.workspace, this.owner, this.property, this.info)
    return this.editable ? can : {link: false, create: false, unlink: false}
  }

  /** As preferências deste quadro são da propriedade, não da coleção de destino. */
  get scope(): string {
    return relationshipScope(this.ownerContext.idea, this.property.alias)
  }

  /**
   * Múltiplo direto: o filho nasce solto, porque a lista mora no dono. Duas escritas, sem
   * atomicidade — se o patch falhar, sobra um filho sem vínculo, e por isso o erro sobe em vez
   * de ser engolido.
   *
   * Função, e não evento, porque precisa ser aguardada antes de o quadro recarregar: sem o
   * vínculo o filho recém-criado não cairia no filtro.
   */
  // eslint-disable-next-line no-unused-vars
  get linkCreated(): ((added: any) => Promise<void>) | null {
    if (!needsLinkAfterCreate(this.property)) return null
    //Só o vínculo: quem relê é o `reload` do quadro, logo em seguida, e ele passa pelo dono
    //(`reloadItems`). Reler aqui seria a mesma leitura duas vezes.
    return async (added: any) => linkToOwner(this.$instances, this.ownerContext, this.owner, this.property,
        [Number(added._instance)])
  }

  /** O "+" de uma raia: o mesmo "Adicionar", aberto nele — ver o `addInLane` do KanbanBoard. */
  openInLane(element: Element, lane: LaneAdd) {
    (this.$refs.add as any)?.openInLane(element, lane)
  }

  askUnlink(item: any) {
    this.unlinking = item
  }

  /** Repassa com a prévia que o quadro montou: a ficha mostra já, e a edição (o lápis) abre já. */
  openInstance(context: InstanceContext, preview: Preview | null) {
    this.$emit('open-instance', context, preview)
  }

  onAdded(items: any[]) {
    this.$emit('update:owner', withRelated(this.owner, this.property, [...this.items, ...items]))
  }

  onUnlinked(id: number) {
    this.unlinking = null
    this.$emit('update:owner',
        withRelated(this.owner, this.property, this.items.filter(item => Number(item._instance) !== id)))
  }
}

export default toNative(RelationshipBoard)
</script>

<style scoped>
/* A frente da linha de botões do cabeçalho (ver o #controls acima) */
.board-add--first {
  order: -1;
}
</style>
